/**
 * 公開中リスト（アコーディオン）＋名前クリックで地図移動（要件14）。
 *
 * - 人数バッジをクリックで開閉
 * - 開くと公開中（visible=true でサーバーが返した）参加者の表示名一覧（1人でもリスト）
 * - 名前クリックで onSelect（地図をその参加者へ移動）
 *
 * 配置（左上・幅）は App の入れ物が持つ（真下にライブアクティビティフィードを並べるため・タスク54）。
 * 開閉状態も App が持つ（開いている間はフィードを止める）。
 */
import type { UserLocation } from '../api/client';
import type { Lang } from '../i18n/lang';
import { messages } from '../i18n/messages';

export interface ParticipantPanelProps {
  readonly users: UserLocation[];
  readonly loading: boolean;
  readonly error: boolean;
  readonly lang: Lang;
  /** 名前クリック時。対象の座標と user_id を渡す（軌跡強調用）。 */
  readonly onSelect: (lng: number, lat: number, userId: string) => void;
  /** アコーディオンの開閉。 */
  readonly open: boolean;
  readonly onOpenChange: (open: boolean) => void;
}

export function ParticipantPanel({
  users,
  loading,
  error,
  lang,
  onSelect,
  open,
  onOpenChange,
}: ParticipantPanelProps) {
  const m = messages(lang);

  const headerText = loading
    ? m.participant.loading
    : error
      ? m.participant.error
      : m.participant.header(users.length);

  return (
    <div
      style={{
        borderRadius: 8,
        background: 'rgba(0,0,0,0.7)',
        color: '#fff',
        font: '13px/1.4 system-ui, sans-serif',
        overflow: 'hidden',
      }}
    >
      <button
        type="button"
        onClick={() => onOpenChange(!open)}
        style={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          width: '100%',
          padding: '8px 10px',
          background: 'transparent',
          border: 'none',
          color: '#fff',
          font: 'inherit',
          cursor: 'pointer',
        }}
        aria-expanded={open}
      >
        <span>{headerText}</span>
        {!loading && !error && users.length > 0 && (
          <span style={{ fontSize: 11 }}>{open ? '▲' : '▼'}</span>
        )}
      </button>

      {open && users.length > 0 && (
        <ul style={{ listStyle: 'none', margin: 0, padding: 0 }}>
          {users.map((u) => (
            <li key={u.user_id}>
              <button
                type="button"
                onClick={() =>
                  onSelect(u.latest.lng, u.latest.lat, u.user_id)
                }
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: 8,
                  width: '100%',
                  padding: '6px 10px',
                  background: 'transparent',
                  border: 'none',
                  borderTop: '1px solid rgba(255,255,255,0.15)',
                  color: '#fff',
                  font: 'inherit',
                  textAlign: 'left',
                  cursor: 'pointer',
                }}
                title={
                  u.status_message
                    ? `${u.display_name}: ${u.status_message}`
                    : u.display_name
                }
              >
                {u.avatar_url ? (
                  <img
                    src={u.avatar_url}
                    alt=""
                    width={20}
                    height={20}
                    style={{ borderRadius: '50%', objectFit: 'cover' }}
                  />
                ) : (
                  <span
                    style={{
                      display: 'inline-flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      width: 20,
                      height: 20,
                      borderRadius: '50%',
                      background: '#4a5568',
                      fontSize: 11,
                    }}
                  >
                    {u.display_name.trim().charAt(0).toUpperCase() || '?'}
                  </span>
                )}
                <span
                  style={{
                    overflow: 'hidden',
                    textOverflow: 'ellipsis',
                    whiteSpace: 'nowrap',
                  }}
                >
                  {u.display_name}
                </span>
              </button>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}
